import React, { useState, useEffect, useRef } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';

/**
 * Кастомный переключатель языка на базе Google Translate.
 *
 * - Язык страницы по умолчанию: русский (ru) — он же оригинал.
 * - Доступные языки: Русский, English, Oʻzbekcha, Қазақша.
 * - Движок Google Translate подгружается скрытно (один раз на всё приложение),
 *   наружу показывается дропдаун, стилизованный под сайт.
 *
 * Компонент можно вставить в любое место (хедер, меню и т.д.):
 *   <LanguageSwitcher />
 */

const PAGE_LANGUAGE = 'ru';

const LANGUAGES = [
    { code: 'ru', label: 'Русский', short: 'RU', flag: '🇷🇺' },
    { code: 'en', label: 'English', short: 'EN', flag: '🇬🇧' },
    { code: 'uz', label: "Oʻzbekcha", short: 'UZ', flag: '🇺🇿' },
    { code: 'kk', label: 'Қазақша', short: 'KK', flag: '🇰🇿' },
];

const INCLUDED_LANGUAGES = LANGUAGES.map((l) => l.code).join(',');

// ----- Вспомогательные функции для работы с cookie googtrans -----

const readGoogTransCookie = () => {
    const match = document.cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
    if (!match) return null;
    try {
        // формат: /ru/en  ->  ['', 'ru', 'en']
        const parts = decodeURIComponent(match[1]).split('/');
        return parts[2] || null;
    } catch (e) {
        return null;
    }
};

const setGoogTransCookie = (target) => {
    const value = `/${PAGE_LANGUAGE}/${target}`;
    const domain = window.location.hostname;
    // Пишем для текущего домена и для домена с точкой (на поддоменах)
    document.cookie = `googtrans=${value};path=/`;
    document.cookie = `googtrans=${value};path=/;domain=${domain}`;
    if (domain.split('.').length > 1) {
        document.cookie = `googtrans=${value};path=/;domain=.${domain}`;
    }
};

const clearGoogTransCookie = () => {
    const expires = 'Thu, 01 Jan 1970 00:00:00 UTC';
    const domain = window.location.hostname;
    document.cookie = `googtrans=;expires=${expires};path=/`;
    document.cookie = `googtrans=;expires=${expires};path=/;domain=${domain}`;
    if (domain.split('.').length > 1) {
        document.cookie = `googtrans=;expires=${expires};path=/;domain=.${domain}`;
    }
};

// ----- Скрытая инициализация движка Google Translate (единожды) -----

let translateEngineBootstrapped = false;

const bootstrapTranslateEngine = () => {
    if (translateEngineBootstrapped) return;
    translateEngineBootstrapped = true;

    // Скрытый контейнер, в который Google встроит свой <select>
    if (!document.getElementById('google_translate_element')) {
        const hidden = document.createElement('div');
        hidden.id = 'google_translate_element';
        hidden.style.display = 'none';
        document.body.appendChild(hidden);
    }

    // Callback для скрипта Google
    window.googleTranslateElementInit = () => {
        if (
            window.google &&
            window.google.translate &&
            window.google.translate.TranslateElement
        ) {
            // eslint-disable-next-line no-new
            new window.google.translate.TranslateElement(
                {
                    pageLanguage: PAGE_LANGUAGE,
                    includedLanguages: INCLUDED_LANGUAGES,
                    autoDisplay: false,
                },
                'google_translate_element'
            );
        }
    };

    // Подключаем скрипт один раз
    if (!document.getElementById('google-translate-script')) {
        const script = document.createElement('script');
        script.id = 'google-translate-script';
        script.src =
            'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
        script.async = true;
        document.body.appendChild(script);
    }
};

// ----- Применение выбранного языка -----

const applyLanguage = (code) => {
    const trigger = (combo) => {
        combo.value = code;
        combo.dispatchEvent(new Event('change'));
    };

    if (code === PAGE_LANGUAGE) {
        // Возврат к оригиналу: чистим cookie и сбрасываем перевод
        clearGoogTransCookie();
        const combo = document.querySelector('select.goog-te-combo');
        if (combo) {
            trigger(combo);
            // На всякий случай перезагружаем, чтобы убрать остаточный перевод
            window.location.reload();
        } else {
            window.location.reload();
        }
        return;
    }

    setGoogTransCookie(code);
    const combo = document.querySelector('select.goog-te-combo');
    if (combo) {
        trigger(combo);
    } else {
        // Движок ещё не готов — перезагрузка применит cookie
        window.location.reload();
    }
};

export default function LanguageSwitcher({ className = '' }) {
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState(PAGE_LANGUAGE);
    const containerRef = useRef(null);

    // Инициализация движка + чтение текущего языка из cookie
    useEffect(() => {
        bootstrapTranslateEngine();
        const fromCookie = readGoogTransCookie();
        if (fromCookie && LANGUAGES.some((l) => l.code === fromCookie)) {
            setCurrent(fromCookie);
        }
    }, []);

    // Закрытие дропдауна по клику вне
    useEffect(() => {
        const onClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const activeLang =
        LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

    const handleSelect = (code) => {
        setOpen(false);
        if (code === current) return;
        setCurrent(code);
        applyLanguage(code);
    };

    return (
        <div
            ref={containerRef}
            className={`relative notranslate ${className}`}
            translate="no"
        >
            {/* Глобальные стили: прячем стандартный UI и баннер Google */}
            <style>{`
                .goog-te-banner-frame.skiptranslate,
                .goog-te-gadget-icon,
                #goog-gt-tt,
                .goog-te-balloon-frame { display: none !important; }
                .goog-tooltip, .goog-tooltip:hover { display: none !important; }
                .goog-text-highlight {
                    background: none !important;
                    box-shadow: none !important;
                }
                body { top: 0 !important; }
                #google_translate_element { display: none !important; }
                .skiptranslate iframe { visibility: hidden !important; height: 0 !important; }
            `}</style>

            {/* Кнопка-триггер в стиле сайта */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex items-center space-x-2 bg-gradient-to-r from-sky-100 to-emerald-100 hover:from-sky-200 hover:to-emerald-200 px-3 py-2 rounded-full border border-emerald-200 shadow-sm hover:shadow transition-all text-emerald-700"
                title="Сменить язык / Change language"
            >
                <Globe size={16} className="text-emerald-600" />
                <span className="text-sm font-semibold">{activeLang.flag}</span>
                <span className="text-sm font-semibold hidden sm:inline">
                    {activeLang.short}
                </span>
                <ChevronDown
                    size={14}
                    className={`text-emerald-600 transition-transform duration-200 ${
                        open ? 'rotate-180' : ''
                    }`}
                />
            </button>

            {/* Выпадающий список */}
            {open && (
                <div className="absolute right-0 mt-2 w-44 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-emerald-200 overflow-hidden z-[60] animate-fadeIn">
                    <div className="h-1 bg-gradient-to-r from-sky-400 via-emerald-400 to-amber-400" />
                    <div className="py-1">
                        {LANGUAGES.map((lang) => {
                            const isActive = lang.code === current;
                            return (
                                <button
                                    key={lang.code}
                                    type="button"
                                    onClick={() => handleSelect(lang.code)}
                                    className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                                        isActive
                                            ? 'bg-gradient-to-r from-sky-50 to-emerald-50 text-emerald-700 font-semibold'
                                            : 'text-gray-700 hover:bg-emerald-50'
                                    }`}
                                >
                                    <span className="flex items-center space-x-2">
                                        <span className="text-base">{lang.flag}</span>
                                        <span>{lang.label}</span>
                                    </span>
                                    {isActive && (
                                        <Check size={15} className="text-emerald-500" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-6px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn { animation: fadeIn 0.18s ease-out; }
            `}</style>
        </div>
    );
}
